'use client';

import React, { memo, useCallback, useEffect, useState } from 'react';
import { useAuthStore, ROLE_LABELS } from '@/stores/auth-store';
import UserProfileAvatar from '@/components/hrm/common/UserProfileAvatar';
import { getNavSectionsForPermissions } from '@/lib/hrm-navigation';
import { usePermissions } from '@/hooks/use-permissions';
import { useDashboardConfig } from '@/hooks/use-dashboard-config';

interface HRMSidebarProps {
  currentPage: string;
  onNavigate: (page: string) => void;
  isOpen: boolean;
  onClose: () => void;
  isCollapsed: boolean;
  expandedWidth: number;
  collapsedWidth: number;
}

const HRMSidebar = memo(function HRMSidebar({
  currentPage,
  onNavigate,
  isOpen,
  onClose,
  isCollapsed,
  expandedWidth,
  collapsedWidth,
}: HRMSidebarProps) {
  const displayUser = useAuthStore(s => s.displayUser);
  const { permissions, isAdmin, isRolePreview, rolePreviewLabel } = usePermissions();
  const { config } = useDashboardConfig();
  const [searchQuery, setSearchQuery] = useState('');
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});

  const sections = React.useMemo(() => {
    return getNavSectionsForPermissions(permissions, isAdmin, displayUser?.role);
  }, [permissions, isAdmin, displayUser?.role]);

  const roleLabel =
    (isRolePreview ? rolePreviewLabel : null) ??
    config?.variantLabel ??
    displayUser?.variantLabel ??
    (displayUser ? ROLE_LABELS[displayUser.role] : '');

  const handleNavClick = useCallback(
    (pageKey: string) => {
      onNavigate(pageKey);
      onClose();
    },
    [onNavigate, onClose],
  );

  const filteredSections = React.useMemo(() => {
    if (!searchQuery.trim()) return sections;
    const q = searchQuery.toLowerCase();
    return sections
      .map(section => ({
        ...section,
        pages: section.pages.filter(p => p.title.toLowerCase().includes(q)),
      }))
      .filter(section => section.pages.length > 0);
  }, [sections, searchQuery]);

  // Keep the section that owns the active page open
  useEffect(() => {
    const active = sections.find(s => s.pages.some(p => p.key === currentPage));
    if (!active) return;
    setOpenSections(prev => (prev[active.key] ? prev : { ...prev, [active.key]: true }));
  }, [currentPage, sections]);

  // Seed: open Main + Self Service (+ active section) on first load / role change
  useEffect(() => {
    setOpenSections(prev => {
      const next = { ...prev };
      for (const s of sections) {
        if (s.key === 'main' || s.key === 'self' || s.pages.some(p => p.key === currentPage)) {
          next[s.key] = true;
        } else if (next[s.key] === undefined) {
          next[s.key] = false;
        }
      }
      return next;
    });
  }, [sections, currentPage]);

  const toggleSection = useCallback((key: string) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const searching = Boolean(searchQuery.trim());

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-35 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`
          fixed left-0 top-0 h-full z-40
          flex flex-col transition-[width,transform] duration-300 ease-in-out overflow-hidden
          border-r w-[260px] max-lg:w-[260px] lg:[width:var(--hrm-sb-w)]
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
        style={
          {
            ['--hrm-sb-w' as string]: `${isCollapsed ? collapsedWidth : expandedWidth}px`,
            backgroundColor: 'var(--hrm-ms-sidebar-bg)',
            borderColor: 'var(--hrm-ms-sidebar-border)',
          } as React.CSSProperties
        }
      >
        <div
          className={`
            flex items-center h-16 border-b flex-shrink-0 min-h-16 px-3
            ${isCollapsed ? 'max-lg:justify-start lg:justify-center lg:px-1' : 'justify-start'}
          `}
          style={{ borderColor: 'var(--hrm-ms-sidebar-border)' }}
        >
          <div
            className={`
              flex items-center min-w-0 gap-3
              ${isCollapsed ? 'max-lg:justify-start lg:justify-center lg:gap-0' : 'justify-start'}
            `}
          >
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: 'var(--hrm-ms-logo-tile)' }}
            >
              <i className="ri-building-2-fill text-white text-lg" />
            </div>
            <div
              className={`min-w-0 ${isCollapsed ? 'max-lg:block lg:hidden' : 'block'}`}
            >
              <span
                className="text-lg font-extrabold tracking-tight"
                style={{ color: 'var(--hrm-ms-brand-text)' }}
              >
                HRM
              </span>
              <span
                className="block text-[0.625rem] font-medium"
                style={{ color: 'var(--hrm-ms-sidebar-section)' }}
              >
                Management Suite
              </span>
            </div>
          </div>
        </div>

        <div
          className={`
            px-3 py-2
            ${isCollapsed ? 'lg:hidden' : ''}
          `}
        >
          <div className="relative">
            <i
              className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm"
              style={{ color: 'var(--hrm-ms-sidebar-section)' }}
            />
            <input
              type="text"
              placeholder="Search pages..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 rounded-lg text-sm border focus:outline-none focus:border-gray-500 placeholder:text-gray-500"
              style={
                {
                  backgroundColor: 'var(--hrm-ms-sidebar-search-bg)',
                  color: 'var(--hrm-ms-sidebar-search-text)',
                  borderColor: 'var(--hrm-ms-sidebar-search-border)',
                } as React.CSSProperties
              }
            />
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto overflow-x-hidden py-1 px-2 space-y-1 min-h-0">
          {filteredSections.map(section => {
            const isSectionOpen = searching || isCollapsed || openSections[section.key] !== false;
            const hasActive = section.pages.some(p => p.key === currentPage);

            return (
              <div key={section.key} className="pb-0.5">
                <button
                  type="button"
                  onClick={() => toggleSection(section.key)}
                  className={`
                    w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded-md
                    text-[10px] font-bold uppercase tracking-wider transition-colors
                    ${isCollapsed ? 'lg:hidden' : ''}
                    hover:[background-color:var(--hrm-ms-sidebar-link-hover)]
                  `}
                  style={{ color: hasActive ? 'var(--hrm-ms-sidebar-accent)' : 'var(--hrm-ms-sidebar-section)' }}
                  aria-expanded={isSectionOpen}
                >
                  <span className="truncate">{section.label}</span>
                  <span className="flex items-center gap-1.5 flex-shrink-0">
                    <span
                      className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full"
                      style={{
                        backgroundColor: 'var(--hrm-ms-sidebar-search-bg)',
                        color: 'var(--hrm-ms-sidebar-section)',
                      }}
                    >
                      {section.pages.length}
                    </span>
                    <i
                      className={`ri-arrow-down-s-line text-sm transition-transform ${isSectionOpen ? 'rotate-0' : '-rotate-90'}`}
                    />
                  </span>
                </button>

                <div
                  className={`
                    space-y-0.5 overflow-hidden transition-[max-height,opacity] duration-200
                    ${isSectionOpen ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'}
                    ${isCollapsed ? 'lg:max-h-[800px] lg:opacity-100' : ''}
                  `}
                >
                  {section.pages.map(page => {
                    const isActive = currentPage === page.key;
                    return (
                      <button
                        key={page.key}
                        type="button"
                        onClick={() => handleNavClick(page.key)}
                        title={isCollapsed ? page.title : undefined}
                        className={`
                          w-full flex items-center gap-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 text-left
                          border-l-[3px] border-solid
                          ${isActive
                            ? 'font-semibold'
                            : 'border-transparent hover:[background-color:var(--hrm-ms-sidebar-link-hover)] hover:[color:var(--hrm-ms-brand-text)]'
                          }
                          ${isCollapsed ? 'lg:justify-center lg:px-1' : 'pl-[9px] pr-3'}
                          ${isCollapsed && !isActive ? 'lg:[border-left-color:transparent]' : ''}
                        `}
                        style={
                          isActive
                            ? {
                                backgroundColor: 'var(--hrm-ms-sidebar-active-bg)',
                                color: 'var(--hrm-ms-sidebar-active-text)',
                                borderLeftColor: 'var(--hrm-ms-sidebar-accent)',
                              }
                            : {
                                color: 'var(--hrm-ms-sidebar-link)',
                                borderLeftColor: 'transparent',
                              }
                        }
                      >
                        <i
                          className={`${page.icon} text-lg flex-shrink-0 w-5 text-center ${isCollapsed ? 'lg:scale-110' : ''}`}
                        />
                        <span
                          className={`truncate ${isCollapsed ? 'lg:sr-only' : ''}`}
                        >
                          {page.title}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        <div
          className="p-3 border-t flex-shrink-0"
          style={{ borderColor: 'var(--hrm-ms-sidebar-border)' }}
        >
          {displayUser && (
            <div
              className={`
                flex items-center gap-2 px-3 py-2 rounded-lg text-sm
                ${isCollapsed ? 'lg:justify-center lg:px-0' : ''}
              `}
            >
              <div title={isCollapsed ? displayUser.name : undefined}>
                <UserProfileAvatar
                  name={displayUser.name}
                  profileImageUrl={displayUser.profileImageUrl}
                  fallbackClassName="bg-[var(--hrm-ms-logo-tile)]"
                />
              </div>
              <div
                className={`min-w-0 flex-1 ${isCollapsed ? 'lg:hidden' : ''}`}
              >
                <p
                  className="text-xs font-semibold truncate"
                  style={{ color: 'var(--hrm-ms-brand-text)' }}
                >
                  {displayUser.name}
                </p>
                <p
                  className="text-[10px] truncate"
                  style={{ color: 'var(--hrm-ms-sidebar-user-muted)' }}
                >
                  {roleLabel}
                </p>
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
});

export default HRMSidebar;
